import { use, useEffect, useMemo, useState } from 'react';
import { SelectChangeEvent } from '@mui/material/Select';
import { jsonParseWithLowerCaseKeys } from 'shared/utils/string-utils';

import TimeFrame from 'features/residents/models/configurations/time-frame';
import VideoConfiguration from 'features/residents/models/configurations/video-configuration';
import ResidentSettingsProviderContext from 'features/residents/resident-settings/providers/contexts/resident-settings-provider-context';

export enum ActiveOption {
  AlarmsOnly = 0,
  Always = 1,
}

type Props = {
  index: number;
};

type VideoScenarioConfiguration = {
  configuration: VideoConfiguration;
  timeFrameValuesChanged: (newTimeFrames: TimeFrame[] | null, newActiveAllDay: boolean) => void;
  onAlarmActiveChanged: (event: SelectChangeEvent<number>) => void;
  onPrivacyModusChanged: (value: boolean) => void;
  determineSelectedActiveOption: () => ActiveOption;
};

export const useVideoScenarioConfiguration = ({ index }: Props): VideoScenarioConfiguration => {
  const {
    detailEditState: { viewingMode, currentResidentSetting, residentSettingEditForm },
  } = use(ResidentSettingsProviderContext);
  const rawConfiguration = useMemo(
    () => currentResidentSetting?.organisationUnitScenarios[index].configuration,
    [currentResidentSetting, index],
  );

  const [configuration, setConfiguration] = useState(jsonParseWithLowerCaseKeys(rawConfiguration) as VideoConfiguration);

  const scenarioConfigurationUpdated = (newConfiguration: unknown) => {
    residentSettingEditForm.setValue(`organisationUnitScenarios.${index}.configuration`, JSON.stringify(newConfiguration), {
      shouldDirty: true,
    });
  };

  const timeFrameValuesChanged = (newTimeFrames: TimeFrame[] | null, newActiveAllDay: boolean): void => {
    const newConfig = {
      ...configuration,
      timeFrames: newTimeFrames,
      activeAllDay: newActiveAllDay,
    };
    scenarioConfigurationUpdated(newConfig);
    setConfiguration(newConfig);
  };

  const onWhenActiveAlarmChanged = (value: boolean): void => {
    const newConfig = {
      ...configuration,
      whenActiveAlarm: value,
    };
    scenarioConfigurationUpdated(newConfig);
    setConfiguration(newConfig);
  };

  const onPrivacyModusChanged = (value: boolean): void => {
    const newConfig = {
      ...configuration,
      isPrivacyModusEnabled: value,
    };
    scenarioConfigurationUpdated(newConfig);
    setConfiguration(newConfig);
  };

  useEffect(() => {
    residentSettingEditForm.clearErrors(`organisationUnitScenarios.${index}`);
    setConfiguration(jsonParseWithLowerCaseKeys(rawConfiguration) as VideoConfiguration);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawConfiguration, viewingMode]);

  const onAlarmActiveChanged = (event: SelectChangeEvent<number>) => {
    const selectedValue = event.target.value as ActiveOption;
    onWhenActiveAlarmChanged(selectedValue === ActiveOption.AlarmsOnly);
  };

  const determineSelectedActiveOption = (): ActiveOption => (configuration.whenActiveAlarm ? ActiveOption.AlarmsOnly : ActiveOption.Always);

  return {
    configuration,
    timeFrameValuesChanged,
    onAlarmActiveChanged,
    determineSelectedActiveOption,
    onPrivacyModusChanged,
  };
};
